import toast from "react-hot-toast";
import {
  FaCheckCircle,
  FaClock,
  FaEdit,
  FaExclamationTriangle,
} from "react-icons/fa";
import { FaCircleXmark, FaEye, FaTrash } from "react-icons/fa6";
import Swal from "sweetalert2";
import { errorImg, notificationGif, successImg3 } from "../assets";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { twMerge } from "tailwind-merge";
import { HiOutlineBell } from "react-icons/hi";

export const ViewIcon = ({ onClick, className }) => {
  return (
    <FaEye
      className={twMerge(
        "text-xl text-blue-600 cursor-pointer hover:text-blue-800 ease duration-200",
        className
      )}
      onClick={onClick}
    />
  );
};
export const EditIcon = ({ onClick, className }) => {
  return (
    <FaEdit
      className={twMerge(
        "text-xl text-yellow-600 cursor-pointer hover:text-yellow-800 ease duration-200",
        className
      )}
      onClick={onClick}
    />
  );
};
export const DeleteIcon = ({ onClick, className }) => {
  return (
    <FaTrash
      className={twMerge(
        "text-lg text-red-600 cursor-pointer hover:text-red-800 ease duration-200",
        className
      )}
      onClick={onClick}
    />
  );
};

export const StatusColors = ({ status, className, variant = "default", size = "default" }) => {
  const sizeClasses = {
    sm: variant === "dynamic" ? "text-xs px-2 py-0.5 sm:px-2.5 sm:py-1" : "text-xs px-2 py-0.5",
    default: variant === "dynamic" ? "text-xs px-3 py-1.5" : "text-xs px-2.5 py-1"
  };
  
  const statusMap = {
    active: { 
      bg: "bg-green-100", 
      text: "text-green-700", 
      label: "Active",
      dynamicBg: "bg-gradient-to-r from-emerald-400 via-green-500 to-teal-600",
      dynamicText: "text-white",
      dynamicLabel: "Active",
      icon: "✓"
    },
    inactive: { 
      bg: "bg-gray-100", 
      text: "text-gray-700", 
      label: "Inactive",
      dynamicBg: "bg-gradient-to-r from-slate-400 to-gray-500",
      dynamicText: "text-white",
      dynamicLabel: "Inactive",
      icon: "○"
    },
    pending: { 
      bg: "bg-yellow-100", 
      text: "text-yellow-700", 
      label: "Pending",
      dynamicBg: "bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-600",
      dynamicText: "text-white",
      dynamicLabel: "Pending",
      icon: "⏳"
    },
    success: { 
      bg: "bg-green-100", 
      text: "text-green-700", 
      label: "Success",
      dynamicBg: "bg-gradient-to-r from-emerald-400 via-green-500 to-teal-600",
      dynamicText: "text-white",
      dynamicLabel: "Success",
      icon: "✓"
    },
    completed: { 
      bg: "bg-green-100", 
      text: "text-green-700", 
      label: "Completed",
      dynamicBg: "bg-gradient-to-r from-green-400 via-emerald-500 to-teal-600",
      dynamicText: "text-white",
      dynamicLabel: "Completed",
      icon: "✓"
    },
    failed: { 
      bg: "bg-red-100", 
      text: "text-red-700", 
      label: "Failed",
      dynamicBg: "bg-gradient-to-r from-red-400 via-rose-500 to-pink-600",
      dynamicText: "text-white",
      dynamicLabel: "Failed",
      icon: "✕"
    },
    cancelled: { 
      bg: "bg-red-100", 
      text: "text-red-700", 
      label: "Cancelled",
      dynamicBg: "bg-gradient-to-r from-red-500 via-rose-600 to-red-700",
      dynamicText: "text-white",
      dynamicLabel: "Cancelled",
      icon: "✕"
    },
    approved: { 
      bg: "bg-green-100", 
      text: "text-green-700", 
      label: "Approved",
      dynamicBg: "bg-gradient-to-r from-emerald-400 via-green-500 to-teal-600",
      dynamicText: "text-white",
      dynamicLabel: "Approved",
      icon: "✓"
    },
    rejected: { 
      bg: "bg-red-100", 
      text: "text-red-700", 
      label: "Rejected",
      dynamicBg: "bg-gradient-to-r from-red-400 via-rose-500 to-orange-600",
      dynamicText: "text-white",
      dynamicLabel: "Rejected",
      icon: "✕"
    },
    reverted: { 
      bg: "bg-orange-100", 
      text: "text-orange-700", 
      label: "Reverted",
      dynamicBg: "bg-gradient-to-r from-orange-400 via-amber-500 to-yellow-600",
      dynamicText: "text-white",
      dynamicLabel: "Reverted",
      icon: "↩"
    },
    compliance: { 
      bg: "bg-purple-100", 
      text: "text-purple-700", 
      label: "Compliance",
      dynamicBg: "bg-gradient-to-r from-purple-400 via-violet-500 to-indigo-600",
      dynamicText: "text-white",
      dynamicLabel: "Compliance",
      icon: "⚖"
    },
    postponed: { 
      bg: "bg-blue-100", 
      text: "text-blue-700", 
      label: "Postponed",
      dynamicBg: "bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-600",
      dynamicText: "text-white",
      dynamicLabel: "Postponed",
      icon: "⏸"
    },
    expired: { 
      bg: "bg-red-100", 
      text: "text-red-700", 
      label: "Expired",
      dynamicBg: "bg-gradient-to-r from-red-600 via-red-700 to-red-800",
      dynamicText: "text-white",
      dynamicLabel: "Expired",
      icon: "⚠"
    },
  };

  const statusText = status ? status.toLowerCase() : "NA";
  const config = statusMap[statusText] || { 
    bg: "bg-gray-100", 
    text: "text-gray-700", 
    label: statusText,
    dynamicBg: "bg-gradient-to-r from-gray-400 to-slate-500",
    dynamicText: "text-white",
    dynamicLabel: statusText,
    icon: "◆"
  };

  if (variant === "dynamic") {
    return (
      <span className={twMerge(`${config.dynamicBg} ${config.dynamicText} ${sizeClasses[size]} font-semibold rounded-full shadow-md`, className)}>
        {config.dynamicLabel || statusText.toUpperCase()}
      </span>
    );
  }

  return (
    <span className={twMerge(`${config.bg} ${config.text} ${sizeClasses[size]} font-bold rounded-full`, className)}>
      {config.label || statusText.toUpperCase()}
    </span>
  );
};

export const TransactionTypeBadge = ({ type, className }) => {
  const typeMap = {
    remit: { bg: "bg-blue-100", text: "text-blue-700", label: "Remit" },
    power: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Power" },
    topup: { bg: "bg-green-100", text: "text-green-700", label: "Top-Up" },
    withdrawal: { bg: "bg-red-100", text: "text-red-700", label: "Withdrawal" },
    refund: { bg: "bg-orange-100", text: "text-orange-700", label: "Refund" },
    paybill: { bg: "bg-purple-100", text: "text-purple-700", label: "Pay Bill" },
    banking: { bg: "bg-indigo-100", text: "text-indigo-700", label: "Banking" },
    event: { bg: "bg-pink-100", text: "text-pink-700", label: "Event" },
    wallet_transaction: { bg: "bg-cyan-100", text: "text-cyan-700", label: "Wallet" },
    agent_transaction: { bg: "bg-teal-100", text: "text-teal-700", label: "Agent" },
    product: { bg: "bg-lime-100", text: "text-lime-700", label: "Product" },
    kalpeh: { bg: "bg-amber-100", text: "text-amber-700", label: "Kalpeh" },
    tickettransfer: { bg: "bg-fuchsia-100", text: "text-fuchsia-700", label: "Ticket Transfer" },
    airtime: { bg: "bg-sky-100", text: "text-sky-700", label: "Airtime" },
    cashback: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Cashback" },
    "remit-reversal": { bg: "bg-rose-100", text: "text-rose-700", label: "Remit Reversal" },
    salary: { bg: "bg-violet-100", text: "text-violet-700", label: "Salary" },
    expense: { bg: "bg-red-100", text: "text-red-700", label: "Expense" },
    payout: { bg: "bg-blue-100", text: "text-blue-700", label: "Payout" },
    vault_to_cashier: { bg: "bg-green-100", text: "text-green-700", label: "Vault to Cashier" },
    cashier_to_vault: { bg: "bg-orange-100", text: "text-orange-700", label: "Cashier to Vault" },
    agent_payout: { bg: "bg-purple-100", text: "text-purple-700", label: "Agent Payout" },
    vault_bank_deposit: { bg: "bg-indigo-100", text: "text-indigo-700", label: "Vault Bank Deposit" },
    pos: { bg: "bg-pink-100", text: "text-pink-700", label: "POS" },
    event_to_merchant_wallet: { bg: "bg-cyan-100", text: "text-cyan-700", label: "Event to Wallet" },
    salary_reversal: { bg: "bg-rose-100", text: "text-rose-700", label: "Salary Reversal" },
  };

  const typeText = type ? type.toLowerCase() : "NA";
  const config = typeMap[typeText] || { 
    bg: "bg-gray-100", 
    text: "text-gray-700", 
    label: type || "N/A"
  };

  return (
    <span className={twMerge(`${config.bg} ${config.text} text-xs px-2.5 py-1 font-semibold rounded-full uppercase`, className)}>
      {config.label}
    </span>
  );
};

export const KYCStatusBadge = ({ status }) => {
  const statusMap = {
    Verified: {
      text: "Verified",
      bg: "bg-green-100",
      textColor: "text-green-700",
      icon: <FaCheckCircle className="w-3 h-3 text-green-600" />,
    },
    Rejected: {
      text: "Rejected",
      bg: "bg-red-100",
      textColor: "text-red-700",
      icon: <FaCircleXmark className="w-3 h-3 text-red-600" />,
    },
    Pending: {
      text: "Pending",
      bg: "bg-yellow-100",
      textColor: "text-yellow-700",
      icon: <FaClock className="w-3 h-3 text-yellow-600" />,
    },
    "Not Verified": {
      text: "Not Verified",
      bg: "bg-gray-100",
      textColor: "text-gray-700",
      icon: <FaExclamationTriangle className="w-3 h-3 text-gray-600" />,
    },
  };

  const config = statusMap[status] || statusMap["Not Verified"];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-xs ${config.bg} ${config.textColor}`}>
      {config.icon}
      {config.text}
    </span>
  );
};

export const KYBStatusBadge = ({ status }) => {
  const statusMap = {
    Approved: {
      text: "Verified",
      color: "bg-green-100 text-green-700",
      icon: <FaCheckCircle className="w-3 h-3 text-green-700" />,
    },
    Rejected: {
      text: "Rejected",
      color: "bg-red-100 text-red-700",
      icon: <FaCircleXmark className="w-3 h-3 text-red-700" />,
    },
    Pending: {
      text: "Pending",
      color: "bg-yellow-100 text-yellow-700",
      icon: <FaClock className="w-3 h-3 text-yellow-700" />,
    },
    "Not Verified": {
      text: "Not Verified",
      color: "bg-[lightgray] text-[gray]",
      icon: <FaExclamationTriangle className="w-3 h-3 text-[gray]" />,
    },
  };

  const { text, color, icon } = statusMap[status] || statusMap["Not Verified"];

  return (
    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${color}`}>
      {icon}
      <span className="text-xs font-medium">{text}</span>
    </div>
  );
};

export const monthsOfTheYear = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const deleteSweetAlert = async (
  title,
  showWarning,
  confirmButtonText,
  icon
) =>
  await Swal.fire({
    title: title || "Are you sure?",
    text: showWarning === true && "You won't be able to revert this!",
    icon: icon || "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: confirmButtonText || "Yes, delete it!",
    customClass: {
      title: "text-xl sm:text-2xl",
    },
  });

export const errorSweetAlert = (message) => {
  Swal.fire({
    icon: "error",
    title: "Oops!",
    text: message || "Server error",
    confirmButtonColor: "#3085d6",
    confirmButtonText: "Okay",
  });
};

export const successToast = (message, options = {}) => {
  toast(
    (t) => (
      <div className="min-w-48 flex flex-col gap-2">
        <div className="flex items-center gap-2 justify-between text-primary">
          <p className="text-xs font-semibold">Success!</p>
          <AiOutlineCloseCircle
            className="cursor-pointer text-md"
            onClick={() => toast.dismiss(t.id)}
          />
        </div>
        <div className="flex items-center gap-2 w-full">
          <img
            src={successImg3}
            alt=""
            className="w-[35px] object-scale-down"
          />
          <p className="flex-1 text-xs">{message}</p>
        </div>
      </div>
    ),
    {
      duration: 5000,
      style: {
        marginTop: 24,
      },
      ...options,
    }
  );
};

// export const notificationToast = (message, options = {}) => {
//   toast(
//     (t) => (
//       <div className="min-w-48 flex flex-col gap-2">
//         <div className="flex items-center gap-2 justify-between text-primary">
//           <p className="text-xs font-semibold">New Notification!</p>
//           <AiOutlineCloseCircle
//             className="cursor-pointer text-md"
//             onClick={() => toast.dismiss(t.id)}
//           />
//         </div>
//         <div className="flex items-center gap-2 w-full">
//           <img
//             src={notificationGif}
//             alt=""
//             className="w-[35px] object-scale-down"
//           />
//           <p className="flex-1 text-xs">{message}</p>
//         </div>
//       </div>
//     ),
//     {
//       duration: 5000,
//       style: {
//         marginTop: 24,
//       },
//       ...options,
//     }
//   );
// };

export const notificationToast = (message, options = {}) => {
  toast(
    (t) => (
      <div className="min-w-72 max-w-md bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <HiOutlineBell className="text-white text-sm" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-semibold text-gray-900">
                New Notification
              </p>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <AiOutlineCloseCircle className="text-lg" />
              </button>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{message}</p>
            <p className="text-xs text-gray-500 mt-2">Just now</p>
          </div>
        </div>

        <div className="h-1 bg-gradient-to-r from-primary to-primary/60"></div>
      </div>
    ),
    {
      duration: 6000,
      position: "bottom-right",
      style: {
        background: "transparent",
        boxShadow: "none",
        padding: 0,
        margin: 0,
      },
      ...options,
    }
  );
};

export const errorToast = (message, options = {}) => {
  toast(
    (t) => (
      <div className="min-w-48 flex flex-col gap-2">
        <div className="flex items-center gap-2 justify-between">
          <p className="text-xs font-semibold">Error!</p>
          <AiOutlineCloseCircle
            className="cursor-pointer text-md"
            onClick={() => toast.dismiss(t.id)}
          />
        </div>
        <div className="flex items-center gap-2 w-full">
          <img src={errorImg} alt="" className="w-[35px] object-scale-down" />
          <p className="flex-1 text-xs">{message}</p>
        </div>
      </div>
    ),
    {
      duration: 5000,
      style: {
        marginTop: 24,
      },
      ...options,
    }
  );
};

const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  
  // const development = "https://api.test.zappme.net/api/v2";
  const development = "http://localhost:5002/api/v2";
  const production = "https://api.zappme.net/api/v2";
  
  if (envUrl) {
    return envUrl;
  }
  
  return process.env.NODE_ENV === 'production' ? production : development;
};

export const backendUrl = getBackendUrl();

console.log('🔗 Portal API Base URL:', backendUrl);
console.log('🌍 Environment:', process.env.NODE_ENV);
console.log('🎯 App Environment:', process.env.REACT_APP_ENVIRONMENT);

export const getAddIconUrl = (doc) => {
  const fileExtension = doc?.name?.split(".").pop().toLowerCase();
  switch (fileExtension) {
    case "doc":
    case "docx":
      return "https://cdn.iconscout.com/icon/free/png-256/free-doc-file-1934509-1634559.png?f=webp";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return "https://static-00.iconduck.com/assets.00/gallery-icon-2048x1997-3ifnwllu.png";
    case "pdf":
      return "https://continentaltextile.com.vn/wp-content/uploads/2023/12/pngtree-pdf-file-icon-png-png-image_7965915.png";
    case "xls":
    case "xlsx":
      return "https://cdn3d.iconscout.com/3d/premium/thumb/xls-file-7931962-6294886.png?f=webp";
    case "ppt":
    case "pptx":
      return "https://cdn3d.iconscout.com/3d/premium/thumb/ppt-file-5608814-4687429.png";
    case "mp4":
      return "https://static.vecteezy.com/system/resources/previews/027/292/609/original/mp4-file-format-of-3d-illustration-file-format-3d-concept-3d-rendering-png.png";
    case "mp3":
      return "https://cdn3d.iconscout.com/3d/premium/thumb/mp3-file-6927357-5651099.png";
    default:
      return "https://cdn-icons-png.flaticon.com/512/5064/5064889.png";
  }
};

export const baseUrl = backendUrl?.replace("/api/v2", "");
export const imgBaseUrl = `${baseUrl}/uploads`;

export const selectInputStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "white",
    padding: "2px",
    borderColor: state.isFocused ? "#4682B4" : "rgba(0,0,0,0.3)",
    boxShadow: state.isFocused ? "0 0 0 1px #4682B4" : "none",
    "&:hover": { borderColor: "#4682B4" },
    borderRadius: "0.375rem",
    outline: "none",
    cursor: state.isDisabled ? "not-allowed" : "default",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "rgba(0,0,0,0.5)",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#4682B4",
    color: "white",
    borderRadius: "0.375rem",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "white",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "white",
    ":hover": {
      backgroundColor: "rgba(0,0,0,0.3)",
      color: "white",
      cursor: "pointer",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "0.375rem",
    marginTop: "4px",
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#4682B4"
      : state.isFocused
      ? "rgba(70,130,180,0.1)"
      : "white",
    color: state.isSelected ? "white" : "black",
    cursor: "pointer",
  }),
};

export const permissions = [
  {
    parent: {
      label: "Dashboard",
      value: "1",
      checked: false,
    },
    children: [],
  },
  {
    parent: {
      label: "Business Management",
      value: "2",
      checked: false,
    },
    children: [],
  },
  {
    parent: {
      label: "Orders & Transactions",
      value: "3",
      checked: false,
    },
    children: [],
  },
  {
    parent: {
      label: "Reports & Analytics",
      value: "4",
      checked: false,
    },
    children: [],
  },
  {
    parent: {
      label: "User Management",
      value: "5",
      checked: false,
    },
    children: [],
  },
  {
    parent: {
      label: "Wallet",
      value: "6",
      checked: false,
    },
    children: [],
  },
  {
    parent: {
      label: "System Management",
      value: "7",
      checked: false,
    },
    children: [
      {
        label: "Roles",
        value: "8",
        checked: false,
      },
      {
        label: "Permissions",
        value: "8",
        checked: false,
      },
    ],
  },
];

export const formatMoney = (amount, currency = 'GMD', locale = 'en-GM') => {
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
	}).format(amount);
};

export const eventFormats = [
  "Concert",
  "Sabar",
  "Gamo",
  "Kankurang Festival",
  "Djembe Gathering",
  "Cultural Symposium",
  "Wrestling Tournament",
  "Kora Concert",
  "Reggae Night",
  "Bantaba Dance",
  "Gospel Festival",
  "Traditional Wedding",
  "Community Jamboree",
  "Salsa Gambiana",
  "Contemporary Art Exhibit",
  "Food & Craft Market",
  "Youth Empowerment Forum",
  "Other",
];

export const transactionTypes = {
  remit: "Remit",
  power: "Power",
  topup: "Top-Up",
  withdrawal: "Withdrawal",
  refund: "Refund",
  paybill: "Pay Bill",
  banking: "Banking",
  event: "Event",
  wallet_transaction: "Wallet Transaction",
  agent_transaction: "Agent Transaction",
  product: "Product",
  kalpeh: "Kalpeh",
  ticketTransfer: "Ticket Transfer",
  airtime: "Airtime",
  cashback: "Cashback",
  "remit-reversal": "Remit Reversal",
  salary: "Salary",
  expense: "Expense",
  payout: "Payout",
  vault_to_cashier: "Vault to Cashier",
  cashier_to_vault: "Cashier to Vault",
  agent_payout: "Agent Payout",
  vault_bank_deposit: "Vault Bank Deposit",
  pos: "POS",
  event_to_merchant_wallet: "Event to Merchant Wallet",
  salary_reversal: "Salary Reversal",
};

export const transactionStatuses = {
  pending: "Pending",
  failed: "Failed",
  success: "Success",
  rejected: "Rejected",
  reverted: "Reverted",
  compliance: "Compliance",
};
